// One practice patient's whole record, in one round trip.
//
// `myCaseRecord` is the deepest legitimate query in the contract (core.md §10):
// the patient with their history entries, every encounter with its note,
// diagnoses, rx items and orders, the thread summaries, and the views the BFF
// derives — active problems, current medications, pending orders, the timeline.
// The record screen's four tabs are four readings of THAT ONE ANSWER, which is
// why they do not each fetch: a student switching from خلاصه to خط زمانی is
// re-reading, not re-asking, and four tab-owned queries would give the four
// tabs four different moments of the same patient.
//
// ── `null` is a STATE, not an error ────────────────────────────────────────
//
// `myCaseRecord` answers null both when the patient does not exist and when
// they belong to another student — DELIBERATELY indistinguishable, so an error
// response cannot be used to confirm which ids exist on the platform one id at
// a time (core.md §3.2). There is no `CASE_FORBIDDEN` code and there must not
// be one.
//
// So a null answer is NOT an error: it does not set `error`, it offers no
// retry, and it renders exactly ONE screen — «این بیمار پیدا نشد» — never
// «متعلق به شما نیست», which is the same oracle spoken in Persian.
//
// ── Why the history is re-read on its own after a save ────────────────────
//
// `caseHistory` is redundant with the record's `entries` and the frozen schema
// keeps it anyway, for exactly this: the شرح حال tab saves a section and needs
// the entries back, not the whole record. Re-reading everything after a
// sentence-sized save would repaint the header — the counts, «آخرین ویزیت» —
// under a student who is mid-sentence in a textarea below it.

import React from 'react';

import {
  bffQuery, bffMutation, errorCode, alreadyOpenEncounterId,
} from '../lib/bffClient.js';
import {
  MY_CASE_RECORD_QUERY,
  CASE_HISTORY_QUERY,
  UPDATE_CASE_MUTATION,
  DELETE_CASE_MUTATION,
  ADD_CASE_HISTORY_MUTATION,
  UPDATE_HISTORY_ENTRY_MUTATION,
  DELETE_HISTORY_ENTRY_MUTATION,
  START_ENCOUNTER_MUTATION,
} from '../graphql/documents.js';
import { refreshProfile } from './useMe.js';

/** The empty record, so a screen never has to guard every field it reads. */
const EMPTY_RECORD = {
  case: null,
  encounters: [],
  threads: [],
  activeProblems: [],
  currentMedications: [],
  pendingOrders: [],
  timeline: [],
};

export function useCaseRecord(caseId) {
  const [record, setRecord] = React.useState(EMPTY_RECORD);
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    if (!caseId) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);

    bffQuery(MY_CASE_RECORD_QUERY, { caseId })
      .then((data) => {
        if (!alive) return;
        const next = data?.myCaseRecord || null;
        setRecord(next ? { ...EMPTY_RECORD, ...next } : EMPTY_RECORD);
        setEntries(next?.case?.entries || []);
        // Not an error. See the header: null is "not available", full stop.
        setError(null);
      })
      .catch((err) => {
        if (!alive) return;
        console.error('[useCaseRecord] failed:', errorCode(err) || err?.message);
        setError(err);
        setRecord(EMPTY_RECORD);
        setEntries([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [caseId, nonce]);

  const reload = React.useCallback(() => setNonce((n) => n + 1), []);

  /** Pull the history back without disturbing the header. */
  const reloadHistory = React.useCallback(async () => {
    if (!caseId) return;
    try {
      const data = await bffQuery(CASE_HISTORY_QUERY, { caseId, sections: null });
      setEntries(data?.caseHistory || []);
    } catch (err) {
      console.error('[useCaseRecord] history reload failed:', errorCode(err) || err?.message);
      // Deliberately NOT cleared: the entries on screen are the ones that were
      // read successfully a moment ago, and blanking them because a refetch
      // failed would look to the student like their writing was lost.
    }
  }, [caseId]);

  /**
   * Update the patient. OMITTED FIELDS ARE LEFT ALONE; `''` (or `0` for a
   * number) clears one.
   *
   * The caller builds the input from what actually CHANGED
   * (`buildCaseUpdate` in src/lib/caseForm.js). Sending the whole form every
   * time works only as long as the form holds every field; the day it holds a
   * subset, a student editing the title erases the rest.
   */
  const updateCase = React.useCallback(async (input) => {
    const data = await bffMutation(UPDATE_CASE_MUTATION, { caseId, input });
    const row = data?.updateCase;
    // `updateCase` answers with the CaseRow shape — no `entries` — so the
    // entries already in state are kept rather than overwritten with undefined.
    if (row) {
      setRecord((prev) => ({ ...prev, case: { ...(prev.case || {}), ...row } }));
    }
    return row || null;
  }, [caseId]);

  const deleteCase = React.useCallback(async () => {
    await bffMutation(DELETE_CASE_MUTATION, { caseId });
    refreshProfile();
  }, [caseId]);

  /**
   * Append entries. ALL-OR-NOTHING, and plural for a reason: the dictation flow
   * submits a whole drafted history at once, and half a history is worse than
   * none — a patient whose سابقه landed and whose داروها did not reads to the
   * model as someone on no drugs.
   */
  const addHistory = React.useCallback(async (rows) => {
    const data = await bffMutation(ADD_CASE_HISTORY_MUTATION, { caseId, entries: rows });
    const added = data?.addCaseHistory || [];
    if (added.length) setEntries((prev) => [...prev, ...added]);
    return added;
  }, [caseId]);

  /**
   * Edit one entry.
   *
   * `section` and `source` are not in the update input and cannot be sent:
   * provenance is a fact about how a row came to exist (core.md §3.4), and an
   * editable one would let a voice draft become indistinguishable from a
   * sentence the student wrote themselves.
   */
  const updateEntry = React.useCallback(async (entryId, input) => {
    const data = await bffMutation(UPDATE_HISTORY_ENTRY_MUTATION, { entryId, input });
    const row = data?.updateCaseHistoryEntry;
    if (row) setEntries((prev) => prev.map((e) => (e.id === row.id ? row : e)));
    return row || null;
  }, []);

  const deleteEntry = React.useCallback(async (entryId) => {
    await bffMutation(DELETE_HISTORY_ENTRY_MUTATION, { entryId });
    setEntries((prev) => prev.filter((e) => e.id !== entryId));
  }, []);

  /**
   * Open a visit.
   *
   * ONE open visit per patient is enforced by a partial unique index in the
   * store, not by a check here (core.md §6): a check would race with a second
   * tab. When the store refuses, the BFF answers `ENCOUNTER_ALREADY_OPEN` with
   * the open visit's id in `extensions`, and the RIGHT behaviour is to go
   * there — the student asked to see a visit for this patient, and there is
   * one. So this resolves to `{ encounter, alreadyOpenId }` rather than
   * throwing: `alreadyOpenId` is a destination, not a failure, and only the
   * screen knows to toast «ویزیت بازی وجود داشت» on the way.
   *
   * `CASE_ARCHIVED` and everything else still throws.
   */
  const startEncounter = React.useCallback(async (reason) => {
    try {
      const data = await bffMutation(START_ENCOUNTER_MUTATION, {
        caseId,
        reason: reason ? String(reason) : null,
      });
      return { encounter: data?.startEncounter || null, alreadyOpenId: null };
    } catch (err) {
      const openId = alreadyOpenEncounterId(err);
      if (openId) return { encounter: null, alreadyOpenId: openId };
      throw err;
    }
  }, [caseId]);

  const caseRow = record.case;

  return {
    record,
    caseRow,
    entries,
    encounters: record.encounters || [],
    threads: record.threads || [],
    activeProblems: record.activeProblems || [],
    currentMedications: record.currentMedications || [],
    pendingOrders: record.pendingOrders || [],
    timeline: record.timeline || [],
    loading,
    error,
    // `notFound` is separate from `error` because they are different screens and
    // only one of them gets a retry button.
    notFound: !loading && !error && !caseRow,
    reload,
    reloadHistory,
    updateCase,
    deleteCase,
    addHistory,
    updateEntry,
    deleteEntry,
    startEncounter,
  };
}

export default useCaseRecord;
